/**
 * 超管「工具策展」页的展示态：候选集、默认全选、已下架 / 不符合条件拆标。
 *
 * 准入只调用 `@dosia/shared` 的 `admitToolModel` / `isMediaGenerationEndpoint`，
 * 本文件不写第二份判据。
 */
import {
  admitToolModel,
  AUDIO_TRANSCRIBE_PROVIDERS_IN_PREFERENCE_ORDER,
  canonicalizeGrokMediaModelId,
  getGrokMediaModelContract,
  GROK_MEDIA_CANONICAL_MODEL_IDS,
  isBrainPoolEntry,
  isMediaGenerationEndpoint,
  stripModelIdTagSuffix,
  WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER,
  type ModelCatalogEntry,
  type ToolAdmissionBucket,
  type ToolAdmissionCatalogRow,
  type ToolAdmissionRejection,
  type AudioTranscribeProviderId,
  type ToolAdmissionVerdict,
  type WebSearchProviderId,
} from "@dosia/shared";

export type CurationWarning = "retired" | "ineligible";

export interface CandidateRef {
  modelId: string;
  protocol?: string;
}

export interface ListedModel {
  modelId: string;
  protocol?: string;
  enabled: boolean;
  warning?: CurationWarning;
  rejection?: ToolAdmissionRejection;
}

export interface ListedWebSearch {
  provider: WebSearchProviderId;
  group: string;
  enabled: boolean;
}

export interface ListedAudioTranscribe {
  provider: AudioTranscribeProviderId;
  group: string;
  enabled: boolean;
}

export interface VideoGenFlags {
  generate: boolean;
  edit: boolean;
  extend: boolean;
}

export type CurationListBucket =
  | "vision"
  | "document"
  | "video-understand"
  | "image-gen"
  | "video-gen"
  | "work";

/** 一次建好，渲染路径里 O(1) 查，禁止每行扫一遍 catalog。 */
export interface CatalogIndex {
  catalog: readonly ModelCatalogEntry[];
  rowsById: Map<string, ModelCatalogEntry[]>;
  rowsByBase: Map<string, ModelCatalogEntry[]>;
  idSet: Set<string>;
  groupById: Map<string, string>;
}

export function buildCatalogIndex(catalog: readonly ModelCatalogEntry[]): CatalogIndex {
  const rowsById = new Map<string, ModelCatalogEntry[]>();
  const rowsByBase = new Map<string, ModelCatalogEntry[]>();
  const idSet = new Set<string>();
  const groupById = new Map<string, string>();
  for (const row of catalog) {
    const list = rowsById.get(row.modelId);
    if (list) list.push(row);
    else rowsById.set(row.modelId, [row]);
    const base = stripModelIdTagSuffix(row.modelId) || row.modelId;
    const byBase = rowsByBase.get(base);
    if (byBase) byBase.push(row);
    else rowsByBase.set(base, [row]);
    idSet.add(row.modelId);
    const canonical = canonicalizeGrokMediaModelId(row.modelId) ?? row.modelId;
    idSet.add(canonical);
    if (row.chosenGroup?.name) {
      if (!groupById.has(row.modelId)) groupById.set(row.modelId, row.chosenGroup.name);
      if (!groupById.has(canonical)) groupById.set(canonical, row.chosenGroup.name);
    }
  }
  return { catalog, rowsById, rowsByBase, idSet, groupById };
}

export function uniqueModelIds(catalog: readonly { modelId: string }[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of catalog) {
    if (!row.modelId || seen.has(row.modelId)) continue;
    seen.add(row.modelId);
    out.push(row.modelId);
  }
  return out;
}

export function catalogHasModel(catalog: readonly { modelId: string }[], modelId: string): boolean {
  const canonical = canonicalizeGrokMediaModelId(modelId) ?? modelId;
  for (const row of catalog) {
    if (row.modelId === modelId || row.modelId === canonical) return true;
    const rowCanonical = canonicalizeGrokMediaModelId(row.modelId) ?? row.modelId;
    if (rowCanonical === canonical) return true;
  }
  return false;
}

export function catalogHasModelIndexed(index: CatalogIndex, modelId: string): boolean {
  if (index.idSet.has(modelId)) return true;
  const canonical = canonicalizeGrokMediaModelId(modelId);
  return canonical !== undefined && index.idSet.has(canonical);
}

export function defaultGroupIndexed(index: CatalogIndex, modelId: string): string {
  const canonical = canonicalizeGrokMediaModelId(modelId) ?? modelId;
  return index.groupById.get(modelId) ?? index.groupById.get(canonical) ?? "";
}

function rowsForId(index: CatalogIndex, modelId: string): ModelCatalogEntry[] {
  return index.rowsById.get(modelId) ?? [];
}

export function admitIndexed(
  index: CatalogIndex,
  modelId: string,
  bucket: ToolAdmissionBucket,
): ToolAdmissionVerdict {
  const mine = rowsForId(index, modelId);
  if (mine.length > 0) {
    const direct = admitToolModel(mine, modelId, bucket);
    if (direct.ok || direct.reason !== "not-in-catalog") return direct;
  }
  const canonical = canonicalizeGrokMediaModelId(modelId);
  if (canonical && canonical !== modelId) {
    const aliased = rowsForId(index, canonical);
    if (aliased.length > 0) return admitToolModel(aliased, canonical, bucket);
  }
  return { ok: false, reason: "not-in-catalog" };
}

function firstOkIndexed(
  index: CatalogIndex,
  modelId: string,
  buckets: readonly ToolAdmissionBucket[],
): ToolAdmissionVerdict {
  let last: ToolAdmissionVerdict = { ok: false, reason: "not-in-catalog" };
  for (const bucket of buckets) {
    const verdict = admitIndexed(index, modelId, bucket);
    if (verdict.ok) return verdict;
    last = verdict;
  }
  return last;
}

export function admitWorkModelIndexed(index: CatalogIndex, modelId: string): ToolAdmissionVerdict {
  const base = stripModelIdTagSuffix(modelId) || modelId;
  const mine = index.rowsByBase.get(base) ?? rowsForId(index, modelId);
  return admitWorkModel(mine, modelId);
}

export function admitForListBucket(
  index: CatalogIndex,
  modelId: string,
  bucket: CurationListBucket,
): ToolAdmissionVerdict {
  switch (bucket) {
    case "vision":
      return admitIndexed(index, modelId, "vision");
    case "document":
      return admitIndexed(index, modelId, "document");
    case "video-understand":
      return admitIndexed(index, modelId, "video-understand");
    case "image-gen":
      return firstOkIndexed(index, modelId, ["image-generation", "image-editing"]);
    case "video-gen":
      return firstOkIndexed(index, modelId, ["video-generation", "video-editing"]);
    case "work":
      return admitWorkModelIndexed(index, modelId);
  }
}

export function formatProtocolLabel(modelId: string, protocol?: string): string {
  return protocol ? `${modelId} · ${protocol}` : modelId;
}

export function moveIndex<T>(arr: readonly T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return [...arr];
  const next = [...arr];
  const a = next[i];
  const b = next[j];
  if (a === undefined || b === undefined) return next;
  next[i] = b;
  next[j] = a;
  return next;
}

export function toggleListed(storedIds: readonly string[], id: string, enabled: boolean): string[] {
  if (enabled) {
    if (storedIds.includes(id)) return [...storedIds];
    return [...storedIds, id];
  }
  return storedIds.filter((item) => item !== id);
}

/** 精确 id 没命中时，用 Grok 媒体别名再问一次 PR-1 的准入，不自己挑协议。 */
export function admitWithCanonical(
  catalog: readonly ToolAdmissionCatalogRow[],
  modelId: string,
  bucket: ToolAdmissionBucket,
): ToolAdmissionVerdict {
  const direct = admitToolModel(catalog, modelId, bucket);
  if (direct.ok) return direct;
  const canonical = canonicalizeGrokMediaModelId(modelId);
  if (canonical && canonical !== modelId) {
    const aliased = admitToolModel(catalog, canonical, bucket);
    if (aliased.ok) return aliased;
  }
  return direct;
}

function firstOk(
  catalog: readonly ToolAdmissionCatalogRow[],
  modelId: string,
  buckets: readonly ToolAdmissionBucket[],
): ToolAdmissionVerdict {
  let last: ToolAdmissionVerdict = { ok: false, reason: "not-in-catalog" };
  for (const bucket of buckets) {
    const verdict = admitWithCanonical(catalog, modelId, bucket);
    if (verdict.ok) return verdict;
    last = verdict;
  }
  return last;
}

export function admitImageGenModel(
  catalog: readonly ToolAdmissionCatalogRow[],
  modelId: string,
): ToolAdmissionVerdict {
  return firstOk(catalog, modelId, ["image-generation", "image-editing"]);
}

export function admitVideoGenModel(
  catalog: readonly ToolAdmissionCatalogRow[],
  modelId: string,
): ToolAdmissionVerdict {
  return firstOk(catalog, modelId, ["video-generation", "video-editing"]);
}

export function workModelBaseId(modelId: string): string {
  return stripModelIdTagSuffix(modelId) || modelId;
}

/**
 * 干活模型：非主脑池，且至少有一条非生图/生视频端点的行。
 * 端点闸用 PR-1 的 `isMediaGenerationEndpoint`，不另写名单。
 */
export function admitWorkModel(
  catalog: readonly ModelCatalogEntry[],
  modelId: string,
): ToolAdmissionVerdict {
  const base = workModelBaseId(modelId);
  const mine = catalog.filter(
    (row) => row.modelId === modelId || workModelBaseId(row.modelId) === base,
  );
  if (mine.length === 0) return { ok: false, reason: "not-in-catalog" };
  const usable = mine.filter((row) => !isBrainPoolEntry(row));
  if (usable.length === 0) return { ok: false, reason: "capability-missing" };
  const textRows = usable.filter((row) => !isMediaGenerationEndpoint(row.endpointPath));
  if (textRows.length === 0) return { ok: false, reason: "generation-endpoint" };
  const picked = textRows[0];
  if (!picked) return { ok: false, reason: "capability-missing" };
  return { ok: true, row: picked };
}

export function bucketCandidatesFromIndex(index: CatalogIndex, bucket: CurationListBucket): CandidateRef[] {
  const out: CandidateRef[] = [];
  for (const id of index.rowsById.keys()) {
    const verdict = admitForListBucket(index, id, bucket);
    if (verdict.ok) out.push({ modelId: id, protocol: verdict.row.protocol });
  }
  return out;
}

export function bucketCandidates(
  catalog: readonly ModelCatalogEntry[],
  bucket: ToolAdmissionBucket,
): CandidateRef[] {
  const listBucket: CurationListBucket =
    bucket === "document" || bucket === "video-understand" || bucket === "vision"
      ? bucket
      : bucket === "image-generation" || bucket === "image-editing"
        ? "image-gen"
        : bucket === "video-generation" || bucket === "video-editing"
          ? "video-gen"
          : "vision";
  return bucketCandidatesFromIndex(buildCatalogIndex(catalog), listBucket);
}

function overlayGrokCanonicals(
  catalog: readonly ToolAdmissionCatalogRow[],
  kind: "image" | "video",
  admit: (id: string) => ToolAdmissionVerdict,
  into: CandidateRef[],
  seen: Set<string>,
): void {
  for (const id of GROK_MEDIA_CANONICAL_MODEL_IDS) {
    const contract = getGrokMediaModelContract(id);
    if (contract?.kind !== kind) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    const verdict = admit(id);
    into.push({ modelId: id, protocol: verdict.ok ? verdict.row.protocol : undefined });
  }
}

export function imageGenCandidatesFromIndex(index: CatalogIndex): CandidateRef[] {
  const seen = new Set<string>();
  const out: CandidateRef[] = [];
  for (const id of index.rowsById.keys()) {
    const verdict = admitForListBucket(index, id, "image-gen");
    if (!verdict.ok) continue;
    const canonical = canonicalizeGrokMediaModelId(id) ?? id;
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push({ modelId: canonical, protocol: verdict.row.protocol });
  }
  overlayGrokCanonicals(index.catalog, "image", (id) => admitForListBucket(index, id, "image-gen"), out, seen);
  return out;
}

export function videoGenCandidatesFromIndex(index: CatalogIndex): CandidateRef[] {
  const seen = new Set<string>();
  const out: CandidateRef[] = [];
  for (const id of index.rowsById.keys()) {
    const verdict = admitForListBucket(index, id, "video-gen");
    if (!verdict.ok) continue;
    const canonical = canonicalizeGrokMediaModelId(id) ?? id;
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push({ modelId: canonical, protocol: verdict.row.protocol });
  }
  overlayGrokCanonicals(index.catalog, "video", (id) => admitForListBucket(index, id, "video-gen"), out, seen);
  return out;
}

export function workModelCandidatesFromIndex(index: CatalogIndex): CandidateRef[] {
  const out: CandidateRef[] = [];
  for (const base of index.rowsByBase.keys()) {
    const verdict = admitWorkModelIndexed(index, base);
    if (verdict.ok) out.push({ modelId: base, protocol: verdict.row.protocol });
  }
  return out;
}

export function imageGenCandidates(catalog: readonly ModelCatalogEntry[]): CandidateRef[] {
  return imageGenCandidatesFromIndex(buildCatalogIndex(catalog));
}

export function videoGenCandidates(catalog: readonly ModelCatalogEntry[]): CandidateRef[] {
  return videoGenCandidatesFromIndex(buildCatalogIndex(catalog));
}

export function workModelCandidates(catalog: readonly ModelCatalogEntry[]): CandidateRef[] {
  return workModelCandidatesFromIndex(buildCatalogIndex(catalog));
}

export function defaultGroupFor(catalog: readonly ModelCatalogEntry[], modelId: string): string {
  return defaultGroupIndexed(buildCatalogIndex(catalog), modelId);
}

export function videoGenFlagsFromIndex(index: CatalogIndex, modelId: string): VideoGenFlags {
  const generate = admitIndexed(index, modelId, "video-generation").ok;
  const edit = admitIndexed(index, modelId, "video-editing").ok;
  const contract = getGrokMediaModelContract(modelId);
  const fromContract = contract?.paramSchema.mode ?? [];
  const fromRow = (index.rowsById.get(modelId) ?? index.rowsById.get(canonicalizeGrokMediaModelId(modelId) ?? modelId) ?? [])[0];
  const modes = fromRow?.capabilities?.paramSchema?.mode ?? fromContract;
  return { generate, edit, extend: modes.includes("extension") };
}

export function videoGenFlags(
  catalog: readonly ModelCatalogEntry[],
  modelId: string,
): VideoGenFlags {
  return videoGenFlagsFromIndex(buildCatalogIndex(catalog), modelId);
}

/**
 * 已启用在前（含已下架 / 不符合条件，钉在顶部），未启用在后。
 * 空桶且未改过 → 全部候选勾上，顺序 = 候选顺序（网关清单顺序）。
 * 已有配置 → 勾选与顺序照旧，其余进未启用。
 */
export function listBucketModels(input: {
  storedIds: readonly string[];
  candidates: readonly CandidateRef[];
  index: CatalogIndex;
  catalogSource: "live" | "unavailable";
  touched: boolean;
  bucket: CurationListBucket;
}): ListedModel[] {
  const candidateIds = input.candidates.map((item) => item.modelId);
  const candidateById = new Map(input.candidates.map((item) => [item.modelId, item]));

  if (input.storedIds.length === 0 && !input.touched) {
    return input.candidates.map((item) => ({
      modelId: item.modelId,
      protocol: item.protocol,
      enabled: true,
    }));
  }

  const warnings: ListedModel[] = [];
  const normals: ListedModel[] = [];
  const storedSet = new Set(input.storedIds);
  const storedCanonical = new Set(
    input.storedIds.map((id) => canonicalizeGrokMediaModelId(id) ?? id),
  );

  for (const id of input.storedIds) {
    if (!id.trim()) continue;
    const verdict = admitForListBucket(input.index, id, input.bucket);
    const canonical = canonicalizeGrokMediaModelId(id);
    const needsOwnOption = id.length > 0 && !candidateIds.includes(id);
    const retiredFlag = needsOwnOption && !(canonical && candidateIds.includes(canonical));
    const inCatalog = catalogHasModelIndexed(input.index, id);

    if (input.catalogSource === "live" && retiredFlag && !inCatalog) {
      warnings.push({
        modelId: id,
        protocol: candidateById.get(id)?.protocol,
        enabled: true,
        warning: "retired",
        rejection: "not-in-catalog",
      });
      continue;
    }

    if (inCatalog && !verdict.ok) {
      warnings.push({
        modelId: id,
        protocol: candidateById.get(id)?.protocol,
        enabled: true,
        warning: "ineligible",
        rejection: verdict.reason,
      });
      continue;
    }

    normals.push({
      modelId: id,
      protocol: verdict.ok ? verdict.row.protocol : candidateById.get(id)?.protocol,
      enabled: true,
    });
  }

  const disabled: ListedModel[] = [];
  for (const item of input.candidates) {
    if (storedSet.has(item.modelId)) continue;
    if (storedCanonical.has(item.modelId)) continue;
    disabled.push({ modelId: item.modelId, protocol: item.protocol, enabled: false });
  }

  return [...warnings, ...normals, ...disabled];
}

/**
 * 「provider 链」类桶（web_search / audio_transcribe）的展示态 —— 两个桶**同律、同判据**，
 * 所以只有这一份实现：
 *   · 「一条都没存过、且本次也没动过」才等于「未配置」→ 默认全选 + 推荐序；
 *   · 只要存过或动过，就按存量顺序渲染已选，未选的按值域顺序垫在后面。
 *
 * 值域由调用方从 shared 传进来（`WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER` /
 * `AUDIO_TRANSCRIBE_PROVIDERS_IN_PREFERENCE_ORDER`），本文件不抄第二份腿清单——加一条腿
 * 只改 shared，这张列表自动多一行。
 */
function listProviderChain<P extends string>(
  all: readonly P[],
  input: { stored: readonly { provider: P; group: string }[]; touched: boolean },
): { provider: P; group: string; enabled: boolean }[] {
  if (input.stored.length === 0 && !input.touched) {
    return all.map((provider) => ({ provider, group: "", enabled: true }));
  }
  const storedSet = new Set(input.stored.map((row) => row.provider));
  const enabled = input.stored.map((row) => ({ ...row, enabled: true }));
  const disabled = all
    .filter((provider) => !storedSet.has(provider))
    .map((provider) => ({ provider, group: "", enabled: false }));
  return [...enabled, ...disabled];
}

export function listWebSearchProviders(input: {
  stored: readonly { provider: WebSearchProviderId; group: string }[];
  touched: boolean;
}): ListedWebSearch[] {
  return listProviderChain(WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER, input);
}

export function listAudioTranscribeProviders(input: {
  stored: readonly { provider: AudioTranscribeProviderId; group: string }[];
  touched: boolean;
}): ListedAudioTranscribe[] {
  return listProviderChain(AUDIO_TRANSCRIBE_PROVIDERS_IN_PREFERENCE_ORDER, input);
}

export function enabledIdsOf(listed: readonly ListedModel[]): string[] {
  return listed.filter((item) => item.enabled).map((item) => item.modelId);
}
